// @ts-nocheck
import { logger } from '../../../../../shims/appStub';
import { parseDateTimeMs } from '../datetime/parseDateTimeMs';
import {
  ITEM_VARIABLE_POLICY,
  type ItemVariableBag,
  type ItemVariableEntry,
  type ItemVariableErrorCode,
  type ItemVariableType,
  type PersistedItemVariableType,
} from './itemVariablePolicy';
import { coerceListValue } from './listValueUtils';

export type {
  ItemVariableBag,
  ItemVariableEntry,
  ItemVariableType,
  ItemVariableErrorCode,
  PersistedItemVariableType,
};
export { ITEM_VARIABLE_POLICY };

export interface ParseItemVariablesResult {
  bag: ItemVariableBag;
  diagnostic?: string;
}

export interface ReadVariableResult {
  found: boolean;
  value: unknown;
  type: PersistedItemVariableType | null;
}

export type WriteVariableResult =
  | { ok: true; bag: ItemVariableBag; changed: boolean }
  | { ok: false; code: ItemVariableErrorCode; message: string };

export type DeleteVariableResult =
  | { ok: true; bag: ItemVariableBag; changed: boolean }
  | { ok: false; code: ItemVariableErrorCode; message: string };

const COLOR_HEX_PATTERN = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

function defaultValueForType(type: ItemVariableType): unknown {
  switch (type) {
    case 'string':
    case 'color':
      return '';
    case 'integer':
    case 'float':
      return 0;
    case 'boolean':
      return false;
    case 'datetime':
    case 'itemRef':
      return null;
    case 'list':
      return [];
  }
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function isPlainBag(value: unknown): value is ItemVariableBag {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  for (const entry of Object.values(value as Record<string, unknown>)) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      return false;
    }
    const record = entry as Record<string, unknown>;
    if (typeof record.t !== 'string' || !('v' in record)) {
      return false;
    }
  }
  return true;
}

function validateKey(key: string): ItemVariableErrorCode | null {
  if (!key || key.length > ITEM_VARIABLE_POLICY.maxKeyLength) {
    return 'invalid_key';
  }
  if (!ITEM_VARIABLE_POLICY.keyPattern.test(key)) {
    return 'invalid_key';
  }
  return null;
}

function isItemVariableType(value: unknown): value is ItemVariableType {
  return (
    value === 'string'
    || value === 'integer'
    || value === 'float'
    || value === 'boolean'
    || value === 'color'
    || value === 'datetime'
    || value === 'itemRef'
    || value === 'list'
  );
}

/** Unwrap ItemRef-like objects to a numeric item id for storage. */
function coerceItemRefId(value: unknown): number | null {
  let raw: unknown = value;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (!('itemId' in record)) {
      return null;
    }
    raw = record.itemId;
  }
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(num)) {
    return null;
  }
  const truncated = Math.trunc(num);
  if (truncated <= 0) {
    return null;
  }
  return truncated;
}

function coerceStoredValue(type: ItemVariableType, value: unknown): unknown | null {
  if (type === 'list') {
    if (value === null || value === undefined) {
      return [];
    }
    return coerceListValue(value);
  }
  if (type === 'itemRef') {
    if (value === null || value === undefined) {
      return null;
    }
    return coerceItemRefId(value);
  }
  if (value === null || value === undefined) {
    return type === 'datetime' ? null : defaultValueForType(type);
  }
  if (type === 'string') {
    return String(value);
  }
  if (type === 'color') {
    const normalized = String(value);
    return COLOR_HEX_PATTERN.test(normalized) ? normalized : null;
  }
  if (type === 'boolean') {
    return Boolean(value);
  }
  if (type === 'integer') {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return null;
    return Math.trunc(num);
  }
  if (type === 'float') {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  }
  if (type === 'datetime') {
    return parseDateTimeMs(value);
  }
  return null;
}

function rejectNonScalar(value: unknown): boolean {
  return (
    value === null
    || Array.isArray(value)
    || (typeof value === 'object' && value !== null)
  );
}

function isItemRefLikeObject(value: unknown): boolean {
  return (
    typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
    && 'itemId' in (value as Record<string, unknown>)
  );
}

function valuesEqual(a: ItemVariableEntry['v'], b: ItemVariableEntry['v']): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

export function parseItemVariables(json: string): ParseItemVariablesResult {
  if (!json || json.trim() === '') {
    return { bag: {} };
  }
  try {
    const parsed: unknown = JSON.parse(json);
    if (!isPlainBag(parsed)) {
      return { bag: {}, diagnostic: 'Invalid item variables JSON shape.' };
    }
    return { bag: parsed };
  } catch {
    return { bag: {}, diagnostic: 'Corrupt itemVariablesJson.' };
  }
}

export function serializeItemVariables(bag: ItemVariableBag): string {
  if (Object.keys(bag).length === 0) {
    return '';
  }
  return JSON.stringify(bag);
}

export function readVariable(
  bag: ItemVariableBag,
  key: string,
  expectedType?: ItemVariableType,
): ReadVariableResult {
  const entry = bag[key];
  if (!entry) {
    const fallbackType = expectedType ?? 'string';
    return {
      found: false,
      value: defaultValueForType(fallbackType),
      type: null,
    };
  }
  return {
    found: true,
    value: entry.v,
    type: entry.t,
  };
}

function wouldExceedJsonLimit(bag: ItemVariableBag): boolean {
  return byteLength(serializeItemVariables(bag)) > ITEM_VARIABLE_POLICY.maxJsonBytes;
}

export function writeVariable(
  bag: ItemVariableBag,
  key: string,
  type: ItemVariableType,
  value: unknown,
): WriteVariableResult {
  const keyError = validateKey(key);
  if (keyError) {
    return { ok: false, code: keyError, message: 'Invalid item variable key.' };
  }
  if (!isItemVariableType(type)) {
    return { ok: false, code: 'invalid_type', message: 'Invalid item variable type.' };
  }
  if (
    rejectNonScalar(value)
    && type !== 'list'
    && !(type === 'itemRef' && (value === null || isItemRefLikeObject(value)))
    && !(type === 'datetime' && value === null)
  ) {
    return { ok: false, code: 'invalid_value', message: 'Item variable value must be scalar.' };
  }

  const existing = bag[key];
  if (existing && existing.t !== type) {
    return {
      ok: false,
      code: 'type_mismatch',
      message: `Key '${key}' is locked to type '${existing.t}'.`,
    };
  }

  if (type === 'list' && Array.isArray(value) && value.length > ITEM_VARIABLE_POLICY.maxListElements) {
    logger.warn(
      'ItemVariableStorage',
      `Item variable list exceeds max of ${ITEM_VARIABLE_POLICY.maxListElements} elements.`,
      { key, length: value.length, cap: ITEM_VARIABLE_POLICY.maxListElements },
    );
    return {
      ok: false,
      code: 'limit_list',
      message: `Item variable list exceeds max of ${ITEM_VARIABLE_POLICY.maxListElements} elements.`,
    };
  }

  const coerced = coerceStoredValue(type, value);
  if (type === 'list') {
    if (coerced === null) {
      return { ok: false, code: 'invalid_value', message: 'Item variable list must be a JSON array.' };
    }
  } else if (type === 'color') {
    if (coerced === null || typeof coerced !== 'string' || !COLOR_HEX_PATTERN.test(coerced)) {
      return { ok: false, code: 'invalid_color', message: 'Color must be #RRGGBB or #RRGGBBAA.' };
    }
  } else if (coerced === null && type !== 'datetime' && type !== 'itemRef') {
    return { ok: false, code: 'invalid_value', message: 'Could not coerce item variable value.' };
  }
  if (type === 'datetime' && coerced === null && value !== null && value !== undefined) {
    return { ok: false, code: 'invalid_value', message: 'Could not coerce item variable value.' };
  }
  if (type === 'itemRef' && coerced === null && value !== null && value !== undefined) {
    return { ok: false, code: 'invalid_value', message: 'Could not coerce item variable value.' };
  }
  if (type === 'string' || type === 'color') {
    const str = String(coerced ?? '');
    if (byteLength(str) > ITEM_VARIABLE_POLICY.maxStringBytes) {
      return { ok: false, code: 'limit_string', message: 'Item variable string exceeds max size.' };
    }
  }

  const isNewKey = !existing;
  if (isNewKey && Object.keys(bag).length >= ITEM_VARIABLE_POLICY.maxKeysPerItem) {
    return { ok: false, code: 'limit_keys', message: 'Item variable key limit reached.' };
  }

  const storedValue = (coerced ?? null) as ItemVariableEntry['v'];
  const nextEntry: ItemVariableEntry = { t: type, v: storedValue };
  const changed =
    !existing || existing.t !== nextEntry.t || !valuesEqual(existing.v, nextEntry.v);
  const nextBag: ItemVariableBag = changed
    ? { ...bag, [key]: nextEntry }
    : bag;

  if (changed && wouldExceedJsonLimit(nextBag)) {
    logger.warn(
      'ItemVariableStorage',
      'Item variables JSON exceeds max size.',
      { key, cap: ITEM_VARIABLE_POLICY.maxJsonBytes },
    );
    return { ok: false, code: 'limit_json', message: 'Item variables JSON exceeds max size.' };
  }

  return { ok: true, bag: nextBag, changed };
}

export function deleteVariable(bag: ItemVariableBag, key: string): DeleteVariableResult {
  const keyError = validateKey(key);
  if (keyError) {
    return { ok: false, code: keyError, message: 'Invalid item variable key.' };
  }
  if (!(key in bag)) {
    return { ok: true, bag, changed: false };
  }
  const nextBag = { ...bag };
  delete nextBag[key];
  return { ok: true, bag: nextBag, changed: true };
}

/** Read raw JSON column from item, write updated JSON string. */
export function readItemVariablesFromColumn(json: string | undefined): ItemVariableBag {
  return parseItemVariables(json ?? '').bag;
}

export function writeItemVariablesToColumn(bag: ItemVariableBag): string {
  return serializeItemVariables(bag);
}
